import { XIcon } from 'lucide-react';
import React from 'react';
import AdvancedFilterTypes from '@/Models/AdvancedFilterTypes';
import UIStrings from '@/Utils/UIStrings';
import { useCandidatesOverviewContext } from '../../Contexts/CandidatesOverviewContext/CandidatesOverviewContext';
import { AdvancedFilter } from '../../Models/AdvancedFilter';
import AdvancedFilterButton from '../Filter/AdvancedFilterButton';
import StatusFilterButton from '../Filter/StatusFilterButton';
import { CustomMenuItemWithValueProps } from '../Filter/CheckboxRow';
import HiringStatusFilterButton from '../Filter/HiringStatusFilterButton';
import { Button } from '@/Components/shadcn';
import { TypographySmall } from '@/Components/shadcn/typography';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/Components/shadcn/tooltip';
import { cn } from '@/lib/utils';

function AdvancedFiltersToolbar(): React.JSX.Element {
    const { advancedFilters, updateAdvancedFilter, resetAdvancedFilterByType, resetAdvancedFilter } =
        useCandidatesOverviewContext();

    const sortFilters = (a: AdvancedFilter, b: AdvancedFilter): number => {
        return a.name?.localeCompare(b?.name);
    };

    const mapFilter2MenuItem = (filter: AdvancedFilter): CustomMenuItemWithValueProps => {
        const { id, name, count, selected } = filter;
        return {
            id,
            menuItemLabel: `${name} (${count})`,
            selected,
            changeCallback: () => {
                updateAdvancedFilter({ ...filter, selected: !selected });
            },
        };
    };

    const getMenuItemsByFilterType = (
        filters: AdvancedFilter[],
        filterType: AdvancedFilterTypes,
    ): CustomMenuItemWithValueProps[] => {
        return filters
            .filter((x) => x.filterType === filterType)
            .sort(sortFilters)
            .map(mapFilter2MenuItem);
    };

    const countryMenuItems = getMenuItemsByFilterType(advancedFilters, AdvancedFilterTypes.Country);
    const educationLevelMenuItems = getMenuItemsByFilterType(
        advancedFilters,
        AdvancedFilterTypes.HighestLevelOfEducation,
    );
    const educationFieldMenuItems = getMenuItemsByFilterType(advancedFilters, AdvancedFilterTypes.FieldOfEducation);
    const jobTypeMenuItems = getMenuItemsByFilterType(advancedFilters, AdvancedFilterTypes.JobType);

    return (
        <div className="flex flex-row flex-wrap items-center gap-2">
            <TypographySmall
                className={cn('text-muted-foreground shrink-0 font-semibold')}
            >{`${UIStrings.Filter}:`}</TypographySmall>
            <StatusFilterButton />
            <HiringStatusFilterButton />
            {countryMenuItems.length > 0 && (
                <AdvancedFilterButton
                    label={UIStrings.Country}
                    menuItems={countryMenuItems}
                    resetCallback={() => resetAdvancedFilterByType(AdvancedFilterTypes.Country)}
                />
            )}
            {educationLevelMenuItems.length > 0 && (
                <AdvancedFilterButton
                    label={UIStrings.HighestLevelOfEducation}
                    menuItems={educationLevelMenuItems}
                    resetCallback={() => resetAdvancedFilterByType(AdvancedFilterTypes.HighestLevelOfEducation)}
                />
            )}
            {educationFieldMenuItems.length > 0 && (
                <AdvancedFilterButton
                    label={UIStrings.FieldOfEducation}
                    menuItems={educationFieldMenuItems}
                    resetCallback={() => resetAdvancedFilterByType(AdvancedFilterTypes.FieldOfEducation)}
                />
            )}
            {jobTypeMenuItems.length > 0 && (
                <AdvancedFilterButton
                    label={UIStrings.JobType}
                    menuItems={jobTypeMenuItems}
                    resetCallback={() => resetAdvancedFilterByType(AdvancedFilterTypes.JobType)}
                />
            )}
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon-xs"
                            className="text-muted-foreground"
                            onClick={resetAdvancedFilter}
                            data-cy="AdvancedFilterReset"
                            aria-label={UIStrings.ClearFilters}
                            type="button"
                        >
                            <XIcon />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>{UIStrings.ClearFilters}</TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
    );
}

export default AdvancedFiltersToolbar;
